import { Ajuda } from '../../design/Ajuda';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { listarEquipe } from '../../acesso';
import type { MembroDaEquipe } from '../../acesso';
import type { Sessao } from '../../auth';
import { hojeBrasilia, lerFolgas, salvarPresencas } from '../../giro';

/**
 * CALENDÁRIO DE FOLGAS (dono, 26/09): escolhe a pessoa e toca nos dias que ela não trabalha. Folga
 * é a presença AUSENTE do dia marcada com antecedência: quem folga não recebe bloco e o bloco dele
 * é dividido entre os outros. Cada dia mostra quem está de folga.
 */

const MESES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];
const SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const dois = (n: number) => String(n).padStart(2, '0');
const primeiroNome = (n: string) => n.split(' ')[0] ?? n;

export function CalendarioDeFolgas({ sessao, aoMudar }: { sessao: Sessao; aoMudar?: () => void }) {
  const hoje = hojeBrasilia();
  const [ano, setAno] = useState(Number(hoje.slice(0, 4)));
  const [mes, setMes] = useState(Number(hoje.slice(5, 7)) - 1);
  const [equipe, setEquipe] = useState<readonly MembroDaEquipe[]>([]);
  const [pessoa, setPessoa] = useState('');
  const [folgas, setFolgas] = useState<readonly { accountId: string; nome: string; dia: string }[]>(
    [],
  );
  const [enviando, setEnviando] = useState<string | null>(null);
  const [falha, setFalha] = useState<string | null>(null);

  const ultimo = new Date(Date.UTC(ano, mes + 1, 0)).getUTCDate();
  const de = `${String(ano)}-${dois(mes + 1)}-01`;
  const ate = `${String(ano)}-${dois(mes + 1)}-${dois(ultimo)}`;
  const carregar = useCallback(() => {
    void lerFolgas(de, ate).then((r) => r.ok && setFolgas(r.dados.folgas));
  }, [de, ate]);
  useEffect(carregar, [carregar]);
  useEffect(() => {
    void listarEquipe().then(
      (r) =>
        r.ok &&
        setEquipe(
          r.dados.pessoas
            .filter((p) => p.accountId && p.situacao === 'ativo')
            .slice()
            .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')),
        ),
    );
  }, []);

  const porDia = useMemo(() => {
    const m = new Map<string, { accountId: string; nome: string }[]>();
    for (const f of folgas) m.set(f.dia, [...(m.get(f.dia) ?? []), f]);
    return m;
  }, [folgas]);

  const andar = (k: number) => {
    const t = mes + k;
    setAno(ano + Math.floor(t / 12));
    setMes(((t % 12) + 12) % 12);
  };

  const alternar = (dia: string) => {
    if (!pessoa || enviando) return;
    const folga = porDia.get(dia)?.some((f) => f.accountId === pessoa) ?? false;
    setFalha(null);
    setEnviando(dia);
    void salvarPresencas(sessao, dia, [
      { accountId: pessoa, estado: folga ? 'PRESENTE' : 'AUSENTE' },
    ]).then((r) => {
      setEnviando(null);
      if (r.tipo === 'confirmada') {
        carregar();
        if (dia === hoje) aoMudar?.();
      } else
        setFalha(
          r.tipo === 'recusada'
            ? r.mensagem
            : 'Sem resposta do servidor. Recarregue antes de repetir.',
        );
    });
  };

  const inicio = new Date(Date.UTC(ano, mes, 1)).getUTCDay();
  const celulas: (string | null)[] = [
    ...Array.from({ length: inicio }, () => null),
    ...Array.from({ length: ultimo }, (_, i) => `${String(ano)}-${dois(mes + 1)}-${dois(i + 1)}`),
  ];
  const nomeDaPessoa = equipe.find((p) => p.accountId === pessoa)?.nome;
  const folgasDaPessoa = pessoa ? folgas.filter((f) => f.accountId === pessoa).length : 0;

  return (
    <div className="cv-cartao gf">
      <div className="gf-topo">
        <div>
          <h2>
            Calendário de folgas
            <Ajuda rotulo="o calendário de folgas">
              Escolha a pessoa e toque nos dias de folga dela. No dia marcado ela já aparece ausente
              e não recebe bloco.
            </Ajuda>
          </h2>
          <p className="cv-cinza cv-peq">
            Quem está de folga não recebe bloco: o bloco dele é dividido entre quem trabalha.
          </p>
        </div>
        <div className="gf-mes">
          <button
            type="button"
            className="cv-botao"
            aria-label="Mês anterior"
            onClick={() => andar(-1)}
          >
            ‹
          </button>
          <b>
            {MESES[mes]} {ano}
          </b>
          <button
            type="button"
            className="cv-botao"
            aria-label="Próximo mês"
            onClick={() => andar(1)}
          >
            ›
          </button>
        </div>
      </div>
      <div className="gf-pessoa">
        <select
          value={pessoa}
          onChange={(e) => setPessoa(e.target.value)}
          aria-label="Pessoa para marcar folga"
        >
          <option value="">Escolha a pessoa para marcar a folga…</option>
          {equipe.map((p) => (
            <option key={p.accountId} value={p.accountId ?? ''}>
              {p.nome}
            </option>
          ))}
        </select>
        {pessoa && (
          <span className="cv-cinza cv-peq">
            {nomeDaPessoa}: {folgasDaPessoa} folga(s) em {MESES[mes]?.toLowerCase()} · toque no dia
            para marcar ou desmarcar
          </span>
        )}
      </div>
      <div className="gf-grade" role="grid">
        {SEMANA.map((s) => (
          <div key={s} className="gf-semana">
            {s}
          </div>
        ))}
        {celulas.map((dia, i) => {
          if (!dia) return <div key={`v${String(i)}`} className="gf-vazio" />;
          const quem = porDia.get(dia) ?? [];
          const minha = pessoa !== '' && quem.some((f) => f.accountId === pessoa);
          const passado = dia < hoje;
          return (
            <button
              key={dia}
              type="button"
              className={[
                'gf-dia',
                minha ? 'gf-dia-minha' : '',
                dia === hoje ? 'gf-dia-hoje' : '',
                passado ? 'gf-dia-passado' : '',
              ].join(' ')}
              disabled={!pessoa || passado || enviando !== null}
              aria-pressed={minha}
              onClick={() => alternar(dia)}
              title={quem.map((f) => f.nome).join(', ')}
            >
              <span className="gf-num">{Number(dia.slice(8))}</span>
              {enviando === dia ? (
                <span className="gf-nome">…</span>
              ) : (
                quem.slice(0, 3).map((f) => (
                  <span
                    key={f.accountId}
                    className={f.accountId === pessoa ? 'gf-nome gf-nome-sel' : 'gf-nome'}
                  >
                    {primeiroNome(f.nome)}
                  </span>
                ))
              )}
              {quem.length > 3 && <span className="gf-mais">+{quem.length - 3}</span>}
            </button>
          );
        })}
      </div>
      {falha && (
        <p className="cv-falha" role="alert">
          {falha}
        </p>
      )}
    </div>
  );
}
